import { z, ZodError } from 'zod';

/**
 * 入力バリデーション失敗を表すエラー。
 * message には利用者向けの日本語メッセージ（改行区切り）が入る。
 */
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * フィールド名（スキーマのキー）→ 利用者向けの日本語ラベル。
 * 個別スキーマに日本語メッセージが設定されていない必須項目の
 * フォールバックとして使用する。
 */
const FIELD_LABELS: Record<string, string> = {
  title: '件名',
  name: '名称',
  nameKana: 'フリガナ',
  customerId: '顧客',
  contactId: '連絡先',
  assignedUserId: '担当者',
  orderId: '受注',
  quoteId: '見積',
  invoiceId: '請求書',
  partnerId: '取引先',
  supplierId: '仕入先',
  subcontractorId: '外注先',
  email: 'メールアドレス',
  phone: '電話番号',
  postalCode: '郵便番号',
  amount: '金額',
  total: '合計金額',
  subtotal: '小計',
  taxAmount: '消費税',
  quantity: '数量',
  unitPrice: '単価',
  issueDate: '発行日',
  dueDate: '支払期日',
  deliveryDate: '納品日',
  orderDate: '発注日',
  paidAt: '入金日',
  validUntil: '有効期限',
};

/** 文字列に日本語（ひらがな・カタカナ・漢字）が含まれるか */
function hasJapanese(text: string): boolean {
  return /[぀-ヿ㐀-鿿]/.test(text);
}

/** issue の対象フィールドの日本語ラベルを取得する */
function labelForIssue(issue: z.ZodIssue): string {
  const key = issue.path.length > 0 ? String(issue.path[issue.path.length - 1]) : '';
  return FIELD_LABELS[key] ?? '入力内容';
}

/** 単一の issue を利用者向けメッセージに変換する */
function messageForIssue(issue: z.ZodIssue): string {
  // スキーマ側で日本語メッセージが指定されていればそれを最優先する
  if (issue.message && hasJapanese(issue.message)) {
    return issue.message;
  }

  const label = labelForIssue(issue);

  // 必須未入力（空文字 / 未指定）は「○○は必須です」に統一する
  if (issue.code === 'too_small' && issue.type === 'string' && issue.minimum === 1) {
    return `${label}は必須です`;
  }
  if (issue.code === 'invalid_type' && issue.received === 'undefined') {
    return `${label}は必須です`;
  }

  return `${label}を正しく入力してください`;
}

/**
 * ZodError を利用者が理解できる日本語メッセージ（改行区切り）へ変換する。
 * 重複するメッセージは1つにまとめる。
 */
export function formatZodError(error: ZodError): string {
  const messages = error.issues.map(messageForIssue);
  const unique = [...new Set(messages)];
  return unique.length > 0 ? unique.join('\n') : '入力内容を確認してください';
}

/**
 * 任意のエラーを利用者向けの日本語メッセージへ変換する。
 * フォーム等の catch 節で `getErrorMessage(err)` として利用できる。
 */
export function getErrorMessage(err: unknown, fallback = '処理に失敗しました'): string {
  if (err instanceof ZodError) return formatZodError(err);
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * スキーマで検証し、失敗時は ZodError の生 JSON ではなく
 * 利用者向けの日本語メッセージを持つ ValidationError を投げる。
 * サービス層の `schema.parse(data)` の置き換えとして使用する。
 */
export function parseOrThrow<T>(schema: z.ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new ValidationError(formatZodError(result.error));
  }
  return result.data;
}
